import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram } from "lucide-react";
import { Logo } from "./Logo";
import { SeasonBanner } from "./SeasonBanner";
import { useLang, INSTAGRAM } from "../lib/i18n";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20, duration: 1.6 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const LINKS = ["about", "services", "results", "skincare", "shop", "gift", "blog", "contact"];
const ROUTES = { blog: "/blog", shop: "/shop" };

export const LangToggle = ({ dark = false, id }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center text-[0.7rem] tracking-[0.2em] ${dark ? "text-[#F6F2EC]" : "text-[#14110F]"}`}>
      {["it", "en"].map((l, i) => (
        <span key={l} className="flex items-center">
          {i > 0 && <span className="mx-1.5 opacity-40">/</span>}
          <button
            data-testid={`${id}-lang-${l}`}
            onClick={() => setLang(l)}
            className={`uppercase transition-opacity duration-300 ${lang === l ? "opacity-100 underline underline-offset-4 decoration-[#C9A66B]" : "opacity-50 hover:opacity-90"}`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
};

export const Nav = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [moved, setMoved] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > window.innerHeight * 0.8);
      setMoved(window.scrollY > 40);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = !scrolled;
  const navigate = useNavigate();
  const go = (id) => { setOpen(false); if (ROUTES[id]) navigate(ROUTES[id]); else scrollToId(id); };

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${scrolled ? "bg-[#F6F2EC]/80 backdrop-blur-xl border-[#ddd4c7]" : moved ? "bg-[#14110F]/75 backdrop-blur-xl border-[#F6F2EC]/10" : "bg-transparent border-transparent"}`}
      >
        <SeasonBanner />
        <div className="max-w-[1440px] mx-auto px-5 sm:px-10 h-20 flex items-center justify-between">
          <button data-testid="nav-logo-btn" onClick={() => go("top")} aria-label="Gioia home">
            <Logo dark={dark} />
          </button>
          <nav className="hidden xl:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l}
                data-testid={`nav-link-${l}`}
                onClick={() => go(l)}
                className={`group relative text-[0.78rem] tracking-[0.16em] uppercase ${dark ? "text-[#F6F2EC]/85" : "text-[#14110F]/80"}`}
              >
                {t.nav[l]}
                <span className="absolute left-0 -bottom-1 h-px w-full origin-right scale-x-0 bg-[#C9A66B] transition-transform duration-500 group-hover:origin-left group-hover:scale-x-100" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-5">
            <div className="hidden sm:block"><LangToggle dark={dark} id="nav" /></div>
            <a href={INSTAGRAM} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="nav-instagram-link" className={`hidden sm:inline-flex transition-colors duration-300 hover:text-[#C9A66B] ${dark ? "text-[#F6F2EC]" : "text-[#14110F]"}`}>
              <Instagram className="w-[18px] h-[18px]" strokeWidth={1.4} />
            </a>
            <button
              data-testid="nav-book-btn"
              onClick={() => go("booking")}
              className={`hidden sm:inline-flex items-center rounded-full px-6 py-2.5 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500 ${dark ? "bg-[#F6F2EC] text-[#14110F] hover:bg-[#C9A66B]" : "bg-[#14110F] text-[#F6F2EC] hover:bg-[#8A6A3A]"}`}
            >
              {t.nav.book}
            </button>
            <button data-testid="nav-mobile-toggle" className={`xl:hidden ${dark ? "text-[#F6F2EC]" : "text-[#14110F]"}`} onClick={() => setOpen(true)} aria-label="Menu">
              <Menu className="w-6 h-6" strokeWidth={1.3} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-[60] bg-[#14110F] text-[#F6F2EC] flex flex-col px-6 py-6"
          >
            <div className="flex items-center justify-between h-14">
              <Logo dark />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close"><X className="w-7 h-7" strokeWidth={1.2} /></button>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-3">
              {[...LINKS, "booking"].map((l, i) => (
                <motion.button
                  key={l}
                  data-testid={`mobile-link-${l}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.06, duration: 0.7 }}
                  onClick={() => go(l)}
                  className="text-left font-serif-d font-light text-[2.6rem] leading-[1.1] sm:text-5xl"
                >
                  {l === "booking" ? <em className="text-[#C9A66B]">{t.nav.book}</em> : t.nav[l]}
                </motion.button>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <LangToggle dark id="mobile" />
              <a href={INSTAGRAM} target="_blank" rel="noreferrer" data-testid="mobile-instagram-link" className="flex items-center gap-2 text-sm text-[#C9A66B]">
                <Instagram className="w-5 h-5" strokeWidth={1.3} /> @gioia.centroestetico
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
